import { BaseWebComponentView } from '../../framework/BaseWebComponentView';
import { GzGenericDetailsSection } from '../../ui/gzGenericDetailsSection';
import { RawEventDeviceInfo, RawEventThreatInfo } from '@nirvana/shared/dtos/types/raw-events/gz-console';
import {
  ActionPermissionState,
  ActionsPermissions,
  DetailsPanelEvents,
  GetEventDetailsResponseFrontend,
} from '../types';
import { ColumnKeys } from '@nirvana/shared/dtos/types/alerts/common';
import { ContentRendererHelper } from '../../alerts/detailsPanel/ContentRendererHelper';
import { ActionTaken } from '@nirvana/shared/dtos/types/raw-events/common';
import { Category } from '@nirvana/shared/dtos/types/shared/threats';
import { elementClassListAdd } from '../../utils/htmlElement.utils';
import { ThreatSpecificsHelper } from '../helpers/ThreatSpecificsHelper';
import { EndpointType } from '@nirvana/shared/dtos/types/shared/filters';
import { SectionAction, SectionActionType, SectionContent, TemplateType } from '../../ui/gzGenericDetailsSection/types';
import { ButtonColors, ButtonTypes } from '../../ui/gzButton/types';
import { GzButtonWebComponent } from '../../ui/gzButton';
import { buildHash, NavigationConstants, NavigationHelper, TaskTarget } from '@nirvana/shared/navigation';
import { LitElement } from 'lit';
import { TemplateState } from '../../types/common/types';
import { GzImageRepoWebComponent } from '../../ui/gzImageRepo';
import { ImageState } from '../../ui/gzImageRepo/types';
import { GzLoaderWebComponent } from '../../ui/gzLoader';
import { ButtonHelper } from '../helpers/ButtonHelper';
import { translateFn } from '../../utils/translator.helper';
import { GzDialogWebComponent } from '../../ui/gzDialog';
import { GzDialog, GzDialogEvents } from '../../ui/gzDialog/component/GzDialog';
import { GzIsolateEndpointTaskWebComponent } from '../../networkV2/networkPopUps/popUps/isolateEndpoint/gzIsolateEndpointTask';
import { NetworkPopUpFooterContentWebComponent } from '../../networkV2/networkPopUps/popUps/NetworkPopUpFooterContentWebComponent';
import { GzMalwareScanWebComponent } from '../../networkV2/networkPopUps/popUps/malware/component/GzMalwareScanWebComponent';
import { MalwareScanHelper } from '../../networkV2/networkPopUps/popUps/malware/component/helpers/malware.scan.helper';
import { ConsoleRequest, DataProviderType } from '../../utils/request';
import { GzToastNotificationWebComponent } from '../../ui/gzToastNotification';
import { ToastActionButtonMode, ToastSeverity } from '../../ui/gzToastNotification/type';

export class AlertsDetailsPanelView extends BaseWebComponentView {
  private renderer: ContentRendererHelper;
  private threatsSpecificsHelper: ThreatSpecificsHelper;
  private buttonHelper: ButtonHelper;

  getDomElements(): void {
    this.dom.$sectionsDiv = this.shadow.getElementById('sections');
    this.dom.$generalActions = this.shadow.getElementById('general-actions');
    this.dom.$alertDetailsPanelTitle = this.shadow.getElementById('alertDetailsPanelTitle');
  }

  setLoadingState() {
    this.resetTemplateState(this.dom.$sectionsDiv);
    this.setTemplateState(this.dom.$sectionsDiv, TemplateState.LOADING);
    this.clearDetailsPanelContent();
    this.setDetailsPanelTitle();

    const loadingTemplate = new GzLoaderWebComponent();
    loadingTemplate.rows = 15;
    loadingTemplate.columns = 2;

    this.dom.$sectionsDiv.appendChild(loadingTemplate);
  }

  removeLoadingState() {
    this.resetTemplateState(this.dom.$sectionsDiv);
  }

  buildSuccessView(data: GetEventDetailsResponseFrontend): void {
    this.resetTemplateState(this.dom.$sectionsDiv);

    this.clearDetailsPanelContent();
    this.renderer = new ContentRendererHelper(this.dependencies);
    this.threatsSpecificsHelper = new ThreatSpecificsHelper(this.renderer, this.dependencies);
    this.buttonHelper = new ButtonHelper(this.dependencies);
    this.renderer.allColumnDetails = data.allColumnsDetails;
    this.buildThreatSection(data.detectionDetails);
    this.buildThreatSpecificsSection(data.detectionDetails, data.permissions);
    this.buildAffectedDeviceSection(data.affectedDevice, data.isPartner, data.permissions);
    this.buildGeneralActionsButtons(data.permissions, data.taskTarget);
    this.setDetailsPanelTitle();
  }

  buildErrorView() {
    this.resetTemplateState(this.dom.$sectionsDiv);
    this.setTemplateState(this.dom.$sectionsDiv, TemplateState.ERROR);
    this.setDetailsPanelTitle();
    this.clearDetailsPanelContent();
    this.buildErrorTemplate();
  }

  buildErrorTemplate() {
    const errorTemplate = new GzImageRepoWebComponent();
    errorTemplate.state = ImageState.ERROR;
    errorTemplate.innerHTML = `
          <gz-title fontSize='13'>${this.dependencies.translateFn(
            'Data loading has failed. Please try to refresh the page and if the issue persists contact Bitdefender Enterprise Support for more information.'
          )}</gz-title>
    `;

    this.dom.$sectionsDiv.appendChild(errorTemplate);
  }

  private clearDetailsPanelContent() {
    this.dom.$sectionsDiv.innerHTML = '';
  }

  private setDetailsPanelTitle() {
    this.dom.$alertDetailsPanelTitle.innerText = this.dependencies.translateFn('Detection details');
  }

  buildThreatSection(data: RawEventThreatInfo): void {
    const threatDetails = new GzGenericDetailsSection();

    threatDetails.sectionTitle = this.dependencies.translateFn('Threat info').toUpperCase();
    threatDetails.sectionContents = [
      {
        label: this.renderer.getColumnNameFromKey(ColumnKeys.THREAT_TYPE),
        content: this.renderer.getContentLabelFromValue(data.threatType, ColumnKeys.THREAT_TYPE),
      },
      {
        label: this.renderer.getColumnNameFromKey(ColumnKeys.MALWARE_NAME),
        content: data.threatName ?? '-',
      },
      {
        label: this.renderer.getColumnNameFromKey(ColumnKeys.LAST_DETECTED),
        content: this.renderer.getLastDetectedOnValue(data.detectedOn as any, this.dependencies.getTimezone()),
        templateType: TemplateType.STRING,
      },
      {
        label: this.renderer.getColumnNameFromKey(ColumnKeys.ACTION_TAKEN),
        content: this.getActionTakenContent(data.actionTaken),
        templateType: TemplateType.STRING,
      },
      {
        label: this.renderer.getColumnNameFromKey(ColumnKeys.DETECTING_MODULE),
        content: this.renderer.getContentLabelFromValue(data.detectingModule, ColumnKeys.DETECTING_MODULE),
      },
      {
        label: this.renderer.getColumnNameFromKey(ColumnKeys.DETECTING_TECHNOLOGY),
        content: this.renderer.getContentLabelFromValue(data.detectingTechnology, ColumnKeys.DETECTING_TECHNOLOGY),
      },
    ];

    if (data.category === Category.CONNECTION || data.category === Category.DEVICE) {
      const index = threatDetails.sectionContents.findIndex(
        section => section.label === this.renderer.getColumnNameFromKey(ColumnKeys.MALWARE_NAME)
      );
      threatDetails.sectionContents.splice(index, 1);
    }

    threatDetails.sectionContents = this.formatContentLabels(threatDetails.sectionContents);
    elementClassListAdd(threatDetails, 'threat-details');
    this.dom.$sectionsDiv.appendChild(threatDetails);
  }

  buildThreatSpecificsSection(data: RawEventThreatInfo, permissions: ActionsPermissions) {
    const threatSpecifics = new GzGenericDetailsSection();
    const specificData = this.threatsSpecificsHelper.getSectionSpecificData(data, permissions);
    threatSpecifics.sectionContents = this.formatContentLabels(specificData.sectionContent);
    threatSpecifics.sectionTitle = specificData.title;
    threatSpecifics.sectionActions = specificData.sectionActions ? specificData.sectionActions : [];

    elementClassListAdd(threatSpecifics, 'threat-specifics');
    this.dom.$sectionsDiv.appendChild(threatSpecifics);
  }

  buildGeneralActionsButtons(permissions: ActionsPermissions, data?: TaskTarget): void {
    this.dom.$generalActions.appendChild(this.buildScanButton(permissions.scan, data));

    const isolateButton = this.buildIsolateButton(permissions.isolate, data);
    if (isolateButton) {
      this.dom.$generalActions.appendChild(isolateButton);
    }
  }

  buildScanButton(state: ActionPermissionState, eventData?: TaskTarget): LitElement {
    const shouldAddHandler = state === ActionPermissionState.ACTIVE;
    const clickHandler = () => {
      const footerContent = new NetworkPopUpFooterContentWebComponent();
      const bodyContent = new GzMalwareScanWebComponent();
      bodyContent.shouldShowNetworkType = true;

      const config: GzDialog = {
        headline: translateFn('Malware scan'),
        bodyContent: bodyContent,
        isOpen: true,
        isWindow: true,
        footerContent: footerContent,
      };

      const dialog = GzDialogWebComponent.buildDialogElement(
        config,
        document.getElementById('mainPanel') as HTMLElement
      );

      footerContent.addEventListener(GzDialogEvents.CANCEL, (): void => {
        dialog.close();
      });
      footerContent.addEventListener(GzDialogEvents.CONFIRM, async (): Promise<void> => {
        const formValue = bodyContent.getFormValue();
        const payload = MalwareScanHelper.transformFormValueToConsoleRequestParams(formValue, [eventData]);

        try {
          await ConsoleRequest.createRequest({
            dataProviderType: DataProviderType.ENDPOINTS,
            endpoint: 'Tasks',
            method: 'createTasks',
            payload: payload,
          });
          GzToastNotificationWebComponent.buildToastWrapperElement({
            message: translateFn('Task created successfully. Check the status here: '),
            hasAction: true,
            actionButtonText: translateFn('Tasks'),
            actionCallback: buildHash('/tasks'),
            severity: ToastSeverity.SUCCESS,
            actionButtonMode: ToastActionButtonMode.LINK,
          });
        } catch (e) {
          GzToastNotificationWebComponent.buildToastWrapperElement({
            message: translateFn('The task could not be created. ') + e.err?.message,
            severity: ToastSeverity.ERROR,
          });
        }

        dialog.close();
      });
    };

    const button = new GzButtonWebComponent();
    button.innerText = this.dependencies.translateFn('Scan');
    button.id = 'scan-button';
    button.classList.add('general-action-buttons');
    button.disabled = !this.buttonHelper.isButtonEnabled(state);

    if (shouldAddHandler) {
      button.addEventListener('click', clickHandler);
    }

    return this.buttonHelper.handleTooltips(button, state) as LitElement;
  }

  buildIsolateButton(state: ActionPermissionState, eventData?: TaskTarget): LitElement | null {
    if (state === ActionPermissionState.MISSING_ISOLATE) {
      return null;
    }

    const shouldAddHandler = state === ActionPermissionState.ACTIVE;
    const clickHandler = () => {
      const footerContent = new NetworkPopUpFooterContentWebComponent();

      const config: GzDialog = {
        headline: translateFn('Isolate endpoint'),
        bodyContent: new GzIsolateEndpointTaskWebComponent(),
        isOpen: true,
        footerContent: footerContent,
        isWindow: false,
      };

      const dialog = GzDialogWebComponent.buildDialogElement(
        config,
        document.getElementById('mainPanel') as HTMLElement
      );

      footerContent.addEventListener(GzDialogEvents.CANCEL, (): void => {
        dialog.close();
      });
      footerContent.addEventListener(GzDialogEvents.CONFIRM, (): void => {
        NavigationHelper.isolateEndpointTask(this.dependencies.Ext, eventData as TaskTarget, true);
        dialog.close();
      });
    };

    const button = new GzButtonWebComponent();
    button.innerText = this.dependencies.translateFn('Isolate');
    button.id = 'isolate-button';
    button.classList.add('general-action-buttons');
    button.color = ButtonColors.SECONDARY;
    button.type = ButtonTypes.QUIET;
    button.disabled = !this.buttonHelper.isButtonEnabled(state);

    if (shouldAddHandler) {
      button.addEventListener('click', clickHandler);
    }

    return this.buttonHelper.handleTooltips(button, state) as LitElement;
  }

  private getActionTakenContent(actionTaken: ActionTaken | undefined): string {
    if (!actionTaken) {
      return '-';
    }
    const numOccurrencesString = `(${this.dependencies.translateFn('{numTimes} times', {
      numTimes: actionTaken.numTimes,
    })})`;

    return this.renderer.getActionTakenValue(actionTaken.action, numOccurrencesString);
  }

  private buildAffectedDeviceSection(
    affectedDevice: RawEventDeviceInfo,
    isPartner: boolean,
    permissions: ActionsPermissions
  ) {
    const affectedDeviceSection = new GzGenericDetailsSection();
    affectedDeviceSection.sectionTitle = this.dependencies.translateFn('Affected endpoint').toUpperCase();

    const affectedDeviceSectionActions: Array<SectionAction> = [
      {
        type: SectionActionType.GZ_BUTTON,
        label: this.dependencies.translateFn('Show in network'),
        eventName: DetailsPanelEvents.NAVIGATE_TO_NETWORK,
        eventData: {
          endpointName: affectedDevice.endpoint.name,
          company: {
            id: affectedDevice.company.id,
            name: affectedDevice.company.name,
            path: affectedDevice.company.path,
          },
        },
        itemConfig: {
          type: ButtonTypes.QUIET,
          color: ButtonColors.PRIMARY,
          disabled: !this.buttonHelper.isButtonEnabled(permissions.network),
          tooltip: this.buttonHelper.getTooltipString(permissions.network),
          id: 'showNetwork',
        },
      },
      {
        label: this.dependencies.translateFn('Show detections'),
        type: SectionActionType.GZ_BUTTON,
        eventName: 'showAlerts',
        eventData: {
          endpointName: affectedDevice.endpoint.name,
        },
        itemConfig: {
          color: ButtonColors.PRIMARY,
          type: ButtonTypes.QUIET,
          id: 'showAlerts',
        },
      },
    ];

    const affectedDeviceSectionContents: Array<SectionContent> = [
      {
        label: this.renderer.getColumnNameFromKey(ColumnKeys.DEVICE_NAME),
        content: this.renderer.buildEndpointNameContent(affectedDevice.endpoint, permissions.endpointDetails),
        templateType: TemplateType.ELEMENT,
      },
    ];

    if (affectedDevice.endpoint.shouldDisplayTags) {
      const tagsContent = this.renderer.buildTagsContent(affectedDevice.endpoint.tags);

      affectedDeviceSectionContents.push({
        label: translateFn('Tags'),
        content: tagsContent,
        templateType: typeof tagsContent === 'string' ? TemplateType.STRING : TemplateType.ELEMENT,
      });
    }

    affectedDeviceSectionContents.push({
      label: this.renderer.getColumnNameFromKey(ColumnKeys.ENDPOINT_TYPE),
      content: this.renderer.getContentLabelFromValue(affectedDevice.endpoint.type, ColumnKeys.ENDPOINT_TYPE),
    });

    if (isPartner) {
      affectedDeviceSectionContents.push({
        label: this.renderer.getColumnNameFromKey(ColumnKeys.COMPANY),
        content: !affectedDevice.company.isRemoved
          ? affectedDevice.company.name
          : affectedDevice.company.name + translateFn('(deleted)'),
      });
    }

    affectedDeviceSectionContents.push(
      {
        label: this.renderer.getColumnNameFromKey(ColumnKeys.IP),
        content: affectedDevice.endpoint.ip ?? '-',
      },
      {
        label: this.dependencies.translateFn('Policy'),
        content: this.renderer.buildPolicyContent(affectedDevice.policy, permissions.policy),
        templateType: TemplateType.ELEMENT,
        clampLine: true,
      }
    );

    if (!affectedDevice.endpoint.lostAccess) {
      affectedDeviceSectionContents.push({
        label: this.dependencies.translateFn('Status'),
        content: this.renderer.buildEndpointOnlineStatus(affectedDevice.endpoint),
        templateType: TemplateType.STRING,
      });
    }

    // Containers & Containers hosts don't have risk management
    const securityRisksDrillDownCommonData = {
      company: {
        id: affectedDevice.company.id || '',
        name: affectedDevice.company.name || '',
      },
      endpoint: {
        id: affectedDevice.endpoint.id || '',
        name: affectedDevice.endpoint.name || '',
      },
    };

    if (
      (affectedDevice.endpoint.type === EndpointType.WORKSTATION ||
        affectedDevice.endpoint.type === EndpointType.SERVER) &&
      securityRisksDrillDownCommonData.company.id &&
      securityRisksDrillDownCommonData.company.name &&
      securityRisksDrillDownCommonData.endpoint.id &&
      securityRisksDrillDownCommonData.endpoint.name
    ) {
      const deviceRiskScoreContent = this.renderer.buildDeviceRiskScore(
        affectedDevice.riskManagement,
        permissions.riskScore,
        securityRisksDrillDownCommonData,
        NavigationConstants.RISK_MANAGEMENT.SUBSECTIONS.DEVICES
      );

      const misconfigurationsCount = this.renderer.buildDeviceRisksCount(
        affectedDevice.riskManagement.misconfigurations,
        permissions.riskScore,
        securityRisksDrillDownCommonData,
        NavigationConstants.RISK_MANAGEMENT.SUBSECTIONS.MISCONFIGURATIONS
      );

      const vulnerabilitiesCount = this.renderer.buildDeviceRisksCount(
        affectedDevice.riskManagement.vulnerabilities,
        permissions.riskScore,
        securityRisksDrillDownCommonData,
        NavigationConstants.RISK_MANAGEMENT.SUBSECTIONS.VULNERABILITIES
      );

      affectedDeviceSectionContents.push(
        {
          label: this.dependencies.translateFn('Endpoint risk score'),
          content: deviceRiskScoreContent,
          templateType: typeof deviceRiskScoreContent === 'string' ? TemplateType.STRING : TemplateType.ELEMENT,
        },
        {
          label: this.dependencies.translateFn('Findings'),
          content: misconfigurationsCount,
          templateType: typeof misconfigurationsCount === 'string' ? TemplateType.STRING : TemplateType.ELEMENT,
        },
        {
          label: this.dependencies.translateFn('Vulnerabilities'),
          content: vulnerabilitiesCount,
          templateType: typeof vulnerabilitiesCount === 'string' ? TemplateType.STRING : TemplateType.ELEMENT,
        }
      );
    }

    if (
      permissions.isolate === ActionPermissionState.ACTIVE ||
      permissions.isolate === ActionPermissionState.ISOLATED
    ) {
      affectedDeviceSectionContents.push({
        label: translateFn('Isolated'),
        content: affectedDevice.endpoint.isIsolated ? translateFn('Yes') : translateFn('No'),
      });
    }

    affectedDeviceSection.sectionContents = this.formatContentLabels(affectedDeviceSectionContents);
    affectedDeviceSection.sectionActions = affectedDeviceSectionActions;

    elementClassListAdd(affectedDeviceSection, 'affected-devices');

    this.dom.$sectionsDiv.appendChild(affectedDeviceSection);
  }

  private formatContentLabels(sectionContents: Array<SectionContent>): Array<SectionContent> {
    return sectionContents.map(sectionContent => ({ ...sectionContent, label: `${sectionContent.label}:` }));
  }
}
